import { ApiError, type ApiConfig, type FeathersJSError } from './types'
import { API_CONFIG } from './apiConfig'

export class ApiClient {
  private config: ApiConfig
  private token: string | null

  constructor(config: ApiConfig = API_CONFIG) {
    this.config = config
    this.token = localStorage.getItem('auth_token')
  }

  private getHeaders(): HeadersInit {
    const headers = { ...this.config.headers }
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    return headers
  }

  async request<T>(
    endpoint: string,
    options: RequestInit & { skipAuthError?: boolean } = {}
  ): Promise<T> {
    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers
        }
      })

      // if unauthorized
      if (response.status === 401 && !options.skipAuthError) {
        window.location.href = '/login?referral=' + window.location.pathname
        throw { code: 401, message: 'Unauthorized' }
      }

      if (!response.ok) {
        throw new ApiError((await response.json()) as FeathersJSError)
      }

      const data = await response.json()
      return data
    } catch (error) {
      throw (error as FeathersJSError).code
        ? error
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).message || 'An error occurred'
    }
  }

  async raw(endpoint: string, options: RequestInit = {}): Promise<Response> {
    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers
        }
      })

      if (!response.ok) {
        throw (await response.json()) as FeathersJSError
      }

      const data = response
      return data
    } catch (error) {
      throw (error as FeathersJSError).code
        ? error
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).message || 'An error occurred'
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request(endpoint, { method: 'GET' })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async post<T, B = any>(endpoint: string, body: B): Promise<T> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async patch<T>(
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body: any
  ): Promise<T> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body)
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request(endpoint, { method: 'DELETE' })
  }

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  setIsSignedUp(signedUp: boolean) {
    if (signedUp) {
      localStorage.setItem('signed_up', 'true')
    } else {
      localStorage.removeItem('signed_up')
    }
  }
}

export const apiClient = new ApiClient()
